"use client";

import { useRouter } from "next/navigation";
import { useSubscription } from "@/app/context/SubscriptionContext";
import { Button, Modal, Callout } from "@/app/components/ui";

// Friendly per-feature copy. Each entry is a short, conversion-ready phrase
// that finishes the sentence "On Free, you can't save ___". Keep the
// language concrete (what the user just tried to do) rather than generic
// ("access this feature").
const FEATURE_MESSAGES = {
  editor: "Access to the video editor",
  export: "Video export",
  api: "API access",
  upload: "Uploading videos",
  recording: "Recording with the desktop app",
  password_protection: "Password-protecting videos",
  download: "Downloads",
  transcription: "Transcripts",
  abr_optimization: "Adaptive bitrate optimization",
  api_keys: "API keys",
  invite: "Inviting collaborators",

  // Embed customizer paywall keys — all reachable from /embed-settings rejection
  // responses. Phrases are framed as the saved setting, not the action.
  advanced_analytics: "Heatmaps & advanced analytics (Pro)",
  engagement_optimization: "Engagement optimization (Pro)",
  ab_testing: "A/B testing (Pro)",
  chapters: "Custom chapter markers",
  subtitles: "Subtitles & captions",
  change_colors: "Custom player themes (Simple, VSL, Clay)",
  resume_play: "Resume-play prompt for returning viewers",
  timeline_progression: "Timeline progression effect",
  editor_export: "Exporting edited videos",
  hosted_video_limit: "Hosting more videos than your current limit",
  domain_whitelist: "Domain whitelisting (Ultra)",
  default: "This feature",
};

// Returns the same FEATURE_MESSAGES copy but trimmed for inline use in a
// sentence. Falls back to the default phrase if the key is unknown.
function describeFeature(key) {
  return FEATURE_MESSAGES[key] || FEATURE_MESSAGES.default;
}

export default function PaywallModal() {
  const router = useRouter();
  const {
    paywallOpen,
    paywallFeature,
    closePaywall,
    hasSubscription,
    hasActiveAny,
  } = useSubscription();

  if (!paywallOpen) return null;

  // `paywallFeature` accepts either a single string key (legacy callsites)
  // or an array of `{ path, requires }` objects from the server-side
  // per-field rejection contract. Normalise to a unique list of feature
  // keys so the modal renders one bullet per restricted setting.
  const featureKeys = (() => {
    if (Array.isArray(paywallFeature)) {
      const keys = paywallFeature
        .map((entry) =>
          typeof entry === "string" ? entry : entry?.requires || "default"
        )
        .filter(Boolean);
      return Array.from(new Set(keys));
    }
    if (typeof paywallFeature === "string") return [paywallFeature];
    return ["default"];
  })();

  // We distinguish "expired sub, please renew" from "Free plan, upgrade to a
  // paid tier" — the language is completely different. `hasActiveAny` is the
  // canonical "has an active sub on either product" signal post-migration;
  // fall back to legacy `hasSubscription` for safety.
  const isActiveSub = hasActiveAny ?? hasSubscription;

  const handleSubscribe = () => {
    closePaywall();
    router.push("/subscribe");
  };

  const isMulti = featureKeys.length > 1;
  const lead = isActiveSub
    ? isMulti
      ? "Your current plan doesn't include these settings:"
      : `Your current plan doesn't include ${describeFeature(featureKeys[0]).toLowerCase()}.`
    : `An active subscription is required to ${describeFeature(featureKeys[0]).toLowerCase()}.`;

  const title = isActiveSub
    ? "Upgrade to save these settings"
    : "Subscription required";

  const ctaLabel = isActiveSub ? "Upgrade plan" : "View plans";

  return (
    <Modal
      isOpen={paywallOpen}
      onClose={closePaywall}
      title={title}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={closePaywall}>
            Keep editing
          </Button>
          <Button variant="primary" onClick={handleSubscribe}>
            {ctaLabel}
          </Button>
        </>
      }
    >
      <Callout tone="info">{lead}</Callout>
      {isMulti && (
        <ul className="m-0 mt-3 pl-5 list-disc text-sm text-gray-300 space-y-1">
          {featureKeys.map((key) => (
            <li key={key}>{describeFeature(key)}</li>
          ))}
        </ul>
      )}
      <p className="m-0 mt-3 text-gray-400 text-sm">
        {isActiveSub
          ? "Upgrade your Embeds plan to unlock the selected settings plus higher hosting and bandwidth limits."
          : "Choose a plan that fits your needs and unlock everything you just tried to save."}
      </p>
    </Modal>
  );
}
